import React, { useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { useDebouncedCallback } from 'use-debounce'

import {
  getBriefProject,
  loadBriefProject,
} from '@disco/state-management/reducers/entities/briefProjects'
import {
  getBrief,
  saveBrief,
} from '@disco/state-management/reducers/entities/briefs'

import { Brief } from 'types/brief'

import { ExternalLinkIcon } from 'views/components/ui/icon/svg-icons'
import { CampaignSaveStatus } from 'views/pages/campaigns/campaign-header'

import DocumentHeadTags from 'components/document-head-tags'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import PageContainer from 'components/ui/page-container'
import PageHeader from 'components/ui/page-header'
import Spinner from 'components/ui/spinner'

import { useInterval } from 'hooks/use-interval'
import { usePrevious } from 'hooks/use-previous'
import { useDispatch, useSelector } from 'hooks/use-redux'

import type { BriefNavSteps } from 'features/briefs/utils/nav'

import { BriefNav } from '../brief-nav'
import { BriefNextButton } from '../brief-next-button'

type Props = {
  children: React.ReactNode
  step: BriefNavSteps
  title: string
}

const SAVE_INTERVAL = 60 * 1000

const BriefPage = ({ children, step, title }: Props) => {
  const params = useParams<{ briefId: string }>()
  const briefId = params.briefId ? parseInt(params.briefId) : 0
  const brief = useSelector(state => getBrief(state, { briefId }))

  const dispatch = useDispatch()
  const previousStep = usePrevious(step, true)

  const debouncedSaveBrief = useDebouncedCallback(
    (brief: Brief) => dispatch(saveBrief(brief)),
    5000,
  )

  useEffect(() => {
    // Save the brief when the step changes
    if (
      brief?.status !== 'sent' &&
      brief?.dirty &&
      !brief?.loading &&
      step !== previousStep
    ) {
      debouncedSaveBrief(brief)
    }
  }, [brief, dispatch, step, previousStep])

  useInterval(
    () => {
      // Save the brief periodically
      if (brief?.dirty && !brief?.loading) {
        debouncedSaveBrief(brief)
      }
    },
    brief?.status !== 'sent' ? SAVE_INTERVAL : null, // Only automatically save the brief if it's not sent
  )

  const briefProject = useSelector(state =>
    getBriefProject(state, { briefProjectId: brief.project?.id }),
  )

  useEffect(() => {
    if (brief.project?.id && !briefProject) {
      dispatch(loadBriefProject(brief.project?.id))
    }
  }, [brief.project?.id, briefProject, dispatch])

  if (!brief) return <Spinner />

  const status = <CampaignSaveStatus campaign={brief} />

  const previewButton = (
    <Button
      appearance="subtle"
      caption="Preview brief"
      iconNode={<ExternalLinkIcon size={16} />}
      link={`/brief-editor/${params.briefId}/${brief.inbox}/preview`}
      linkTarget="_blank"
    />
  )

  const saveButton =
    brief?.status === 'sent' || brief?.sent_status ? (
      <Button
        appearance="subtle"
        caption="Publish update"
        disabled={!brief?.dirty || brief?.saving}
        onClick={() => {
          dispatch(saveBrief(brief))
        }}
      />
    ) : null

  // Determine back link based on whether brief is linked to a project
  const backLink = brief?.project
    ? {
        content: brief.project.name,
        link: `/briefs/projects/${brief.project.id}`,
      }
    : { content: 'Briefs', link: '/briefs' }

  const headerTitle = Boolean(brief?.name) ? brief?.name : 'Untitled Brief'

  return (
    <div className="Page_Content">
      <DocumentHeadTags title={`${title} | DISCO`} />
      <PageHeader
        breadcrumbs={backLink}
        title={headerTitle}
        actions={
          <ButtonContainer>
            {status}
            {saveButton}
            {previewButton}
            <BriefNextButton briefId={params.briefId!} currentStep={step} />
          </ButtonContainer>
        }
      />
      <PageContainer>
        <BriefNav activeStep={step} briefId={briefId} />
        <PageContainer.Section>{children}</PageContainer.Section>
      </PageContainer>
    </div>
  )
}

export { BriefPage }
